import { pdf, Document, Page, View, Image } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { Timestamp } from "firebase/firestore";
import * as React from "react";
import { useState, useCallback } from "react";
import useUserStore from "@/modules/users/store";
import { saveReportHistory } from "../firestore/reports";
import { getTemplateById } from "../firestore/templates";
import { DefaultTemplate } from "../templates/DefaultTemplate";
import type { ReportData, UseReportGeneratorOptions, UseReportGeneratorState } from "../types";
import { htmlToReactPDF, type PageContent } from "../utils/html-to-reactpdf";
import { uploadPDF, generateReportStoragePath, generateReportFilename, downloadBlob } from "../utils/storage";
import { replaceVariables } from "../utils/variable-replacer";

/**
 * A4 dimensions in points (same as TemplatePreview)
 */
const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;

/**
 * Default page styles
 */
const defaultPageStyle: Style = {
  padding: 40,
  fontFamily: "Helvetica",
  fontSize: 12
};

const fullPageStyle: Style = {
  padding: 0,
  fontFamily: "Helvetica",
  fontSize: 12
};

const fullPageBackgroundStyle: Style = {
  width: A4_WIDTH,
  height: A4_HEIGHT
};

const fullPageContentStyle: Style = {
  marginTop: -A4_HEIGHT,
  width: A4_WIDTH,
  height: A4_HEIGHT,
  padding: 30
};

/**
 * Build a PDF document from converted HTML pages
 * Handles both fullpage backgrounds and regular content
 * @param pages
 * @param legacyComponent
 */
function buildPdfDocument(
  pages: PageContent[] | undefined,
  legacyComponent: React.ReactElement | null
): React.ReactElement {
  // If no pages array (legacy mode), wrap component in simple document
  if (!pages || pages.length === 0) {
    return React.createElement(
      Document,
      {},
      React.createElement(Page, { size: "A4", style: defaultPageStyle }, legacyComponent)
    );
  }

  // Build pages from conversion result
  const pageElements = pages.map((page, index) => {
    if (page.isFullPage && page.backgroundImage) {
      // Fullpage with background image
      // Use negative margin technique (same as TemplatePreview) to overlay content on image
      return React.createElement(
        Page,
        { key: `page-${index}`, size: "A4", style: fullPageStyle, wrap: false },
        React.createElement(Image, { src: page.backgroundImage, style: fullPageBackgroundStyle }),
        React.createElement(View, { style: fullPageContentStyle }, page.component)
      );
    }

    if (page.isFullPage) {
      // Fullpage without background (just content, no wrap)
      return React.createElement(
        Page,
        { key: `page-${index}`, size: "A4", style: defaultPageStyle, wrap: false },
        page.component
      );
    }

    // Regular page with padding
    return React.createElement(Page, { key: `page-${index}`, size: "A4", style: defaultPageStyle }, page.component);
  });

  return React.createElement(Document, {}, ...pageElements);
}

/**
 * Hook para geração de relatórios PDF
 *
 * Responsabilidades:
 * 1. Renderizar template com dados
 * 2. Gerar blob do PDF
 * 3. Salvar no Storage (opcional)
 * 4. Salvar histórico (opcional)
 * 5. Download automático
 * @param options
 */
export function useReportGenerator(options: UseReportGeneratorOptions = {}): UseReportGeneratorState {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);

  const { saveToStorage = false, saveHistory = true, autoDownload = true, onSuccess, onError } = options;

  const generatePDF = useCallback(
    async (templateId: string, data: ReportData): Promise<Blob | string> => {
      if (!licenciadaId) {
        throw new Error("Licenciada not selected");
      }

      setIsGenerating(true);
      setProgress(0);
      setError(null);

      try {
        // 1. Validate data (10%)
        setProgress(10);
        if (!data.questionario || !data.empresa || data.grupos.length === 0) {
          throw new Error("Dados insuficientes para gerar relatório");
        }

        if (data.resultados.empresa.length === 0) {
          throw new Error("Resultados da empresa não disponíveis");
        }

        // 2. Fetch template from Firestore (20%)
        setProgress(20);
        const template = await getTemplateById(licenciadaId, templateId);

        let blob: Blob;

        // 3. Check if template has custom HTML content
        if (template?.htmlTemplate) {
          setProgress(30);

          // 3a. Replace variables in HTML template
          // Note: template may have imagens array from newer TemplateDocument structure
          const templateImages = (
            template as { imagens?: Array<{ id: string; nome: string; url: string; tipo: string; createdAt: unknown }> }
          ).imagens;
          const { html: processedHtml, missingVariables } = replaceVariables(
            template.htmlTemplate,
            data,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { images: templateImages as any }
          );

          if (missingVariables.length > 0) {
            console.warn("Template variables not replaced:", missingVariables);
          }

          // 3b. Convert HTML to React-PDF components (pass data for dynamic components like radar)
          setProgress(40);
          console.log("[useReportGenerator] Passing to htmlToReactPDF:", {
            hasData: !!data,
            empresaResults: data.resultados?.empresa?.length ?? 0,
            geralResults: data.resultados?.geral?.length ?? 0,
            categoriaResults: data.resultados?.categoria?.length ?? 0,
            // Log first values to compare
            empresaFirstValue: data.resultados?.empresa?.[0]?.value,
            geralFirstValue: data.resultados?.geral?.[0]?.value,
            categoriaFirstValue: data.resultados?.categoria?.[0]?.value
          });
          const conversionResult = htmlToReactPDF(processedHtml, { reportData: data });

          if (conversionResult.errors.length > 0) {
            console.warn("HTML conversion errors:", conversionResult.errors);
          }

          // 3c. Build PDF document
          setProgress(50);
          const pdfElement = buildPdfDocument(conversionResult.pages, conversionResult.component);

          // 3d. Generate PDF blob
          setProgress(55);
          blob = await pdf(pdfElement).toBlob();
          setProgress(60);
        } else {
          // 3. Use DefaultTemplate as fallback (30%)
          setProgress(30);
          const TemplateComponent = DefaultTemplate;
          const element = <TemplateComponent data={data} />;

          // 4. Generate PDF blob (60%)
          setProgress(40);
          blob = await pdf(element).toBlob();
          setProgress(60);
        }

        // 5. Save to storage if configured (80%)
        let downloadUrl: string | undefined;
        let storagePath: string | undefined;

        if (saveToStorage) {
          setProgress(70);
          storagePath = generateReportStoragePath(licenciadaId, data.empresaId, data.questionarioId, templateId);
          downloadUrl = await uploadPDF(blob, storagePath);
          setProgress(80);
        }

        // 6. Save history if configured (90%)
        if (saveHistory) {
          setProgress(85);

          // Build history data
          const historyData: any = {
            idTemplate: templateId,
            idEmpresa: data.empresaId,
            idQuestionario: data.questionarioId,
            idProjeto: data.projetoId,
            idEmpresaProjeto: data.empresaProjetoId || null, // Pass null if not available
            storagePath: storagePath || "", // Always include, can be empty string
            metadados: {
              tamanhoBytes: blob.size,
              numeroPaginas: 1, // TODO: Calculate actual page count
              dataGeracao: Timestamp.now(),
              versaoTemplate: 1,
              formatoOrigem: "react-pdf" as const
            }
          };

          // Only include urlPDF if it exists (optional field)
          if (downloadUrl) {
            historyData.urlPDF = downloadUrl;
          }

          await saveReportHistory(historyData);
          setProgress(90);
        }

        // 7. Auto download if configured (100%)
        if (autoDownload) {
          setProgress(95);
          const empresaNome = (data.empresa as any).empresaFantasia || (data.empresa as any).razaoSocial || "Empresa";
          const filename = generateReportFilename(empresaNome, data.questionario.nome, data.dataGeracao);
          downloadBlob(blob, filename);
        }

        setProgress(100);
        onSuccess?.(downloadUrl);

        return downloadUrl || blob;
      } catch (error_) {
        const errorObject = error_ instanceof Error ? error_ : new Error(String(error_));
        setError(errorObject);
        onError?.(errorObject);
        throw errorObject;
      } finally {
        setIsGenerating(false);
        // Reset progress after a short delay
        setTimeout(() => setProgress(0), 500);
      }
    },
    [licenciadaId, saveToStorage, saveHistory, autoDownload, onSuccess, onError]
  );

  return {
    generatePDF,
    isGenerating,
    progress,
    error
  };
}
